import React from 'react';
import { Box, Grid, Skeleton, Stack } from '@mui/material';
import ManagementPageLayout, { ManagementPageCard } from './ManagementPageLayout';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';

const headerPrimaryColor = brandAlpha('onPrimary', 0.24);
const headerSecondaryColor = brandAlpha('onPrimary', 0.14);
const contentColor = brandAlpha('text', 0.08);
const contentColorStrong = brandAlpha('text', 0.12);

const cardSectionSx = {
  p: 3,
  border: `1px solid ${BRAND.line}`,
  borderRadius: BRAND_RADIUS.card,
  backgroundColor: BRAND.surface,
};

const baseSkeletonSx = {
  transform: 'none',
  bgcolor: contentColor,
};

const strongSkeletonSx = {
  transform: 'none',
  bgcolor: contentColorStrong,
};

const HeaderSkeleton = ({ showLeadingAvatar = false }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
    {showLeadingAvatar ? (
      <Skeleton
        variant="circular"
        animation="wave"
        width={84}
        height={84}
        sx={{ transform: 'none', bgcolor: headerSecondaryColor }}
      />
    ) : null}

    <Skeleton
      variant="rounded"
      animation="wave"
      sx={{
        width: { xs: '78vw', sm: 420, md: 500 },
        maxWidth: '100%',
        height: { xs: 40, md: 52 },
        transform: 'none',
        bgcolor: headerPrimaryColor,
        borderRadius: 3,
        mx: 'auto',
      }}
    />
    <Skeleton
      variant="rounded"
      animation="wave"
      sx={{
        width: { xs: '88vw', sm: 520, md: 620 },
        maxWidth: '100%',
        height: 18,
        transform: 'none',
        bgcolor: headerSecondaryColor,
        borderRadius: 99,
        mx: 'auto',
      }}
    />
    <Skeleton
      variant="rounded"
      animation="wave"
      sx={{
        width: { xs: '68vw', sm: 360, md: 440 },
        maxWidth: '100%',
        height: 18,
        transform: 'none',
        bgcolor: brandAlpha('onPrimary', 0.12),
        borderRadius: 99,
        mx: 'auto',
      }}
    />
  </Box>
);

const CardsSkeletonContent = ({ withAction = false, cardCount = 6 }) => (
  <Box sx={{ p: 4 }}>
    {withAction ? (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4, pt: 1 }}>
        <Skeleton variant="rounded" animation="wave" width={196} height={48} sx={strongSkeletonSx} />
      </Box>
    ) : null}

    <Grid container spacing={3} p={1} sx={{ pt: 2 }}>
      {Array.from({ length: cardCount }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={`card-skeleton-${index}`}>
          <Box sx={{ ...cardSectionSx, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" animation="wave" width="72%" height={34} sx={strongSkeletonSx} />
                <Skeleton variant="text" animation="wave" width="90%" height={22} sx={baseSkeletonSx} />
                <Skeleton variant="text" animation="wave" width="58%" height={22} sx={baseSkeletonSx} />
              </Box>
              <Stack direction="row" spacing={0.75}>
                <Skeleton variant="circular" animation="wave" width={34} height={34} sx={strongSkeletonSx} />
                <Skeleton variant="circular" animation="wave" width={34} height={34} sx={baseSkeletonSx} />
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              <Skeleton variant="rounded" animation="wave" width={92} height={28} sx={baseSkeletonSx} />
              <Skeleton variant="rounded" animation="wave" width={126} height={28} sx={baseSkeletonSx} />
            </Stack>

            <Box sx={{ pt: 2, mt: 'auto', borderTop: `1px solid ${BRAND.lineSoft}`, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
              <Box>
                <Skeleton variant="text" animation="wave" width="64%" height={18} sx={baseSkeletonSx} />
                <Skeleton variant="text" animation="wave" width="52%" height={28} sx={strongSkeletonSx} />
              </Box>
              <Box>
                <Skeleton variant="text" animation="wave" width="64%" height={18} sx={baseSkeletonSx} />
                <Skeleton variant="text" animation="wave" width="60%" height={28} sx={strongSkeletonSx} />
              </Box>
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const DetailSkeletonContent = () => (
  <Box sx={{ p: 3 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, pb: 3, borderBottom: `1px solid ${BRAND.lineSoft}` }}>
      <Skeleton variant="rounded" animation="wave" width={128} height={40} sx={strongSkeletonSx} />
      <Stack direction="row" spacing={1}>
        <Skeleton variant="circular" animation="wave" width={38} height={38} sx={strongSkeletonSx} />
        <Skeleton variant="circular" animation="wave" width={38} height={38} sx={baseSkeletonSx} />
      </Stack>
    </Box>

    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Box sx={cardSectionSx}>
          <Skeleton variant="text" animation="wave" width="34%" height={34} sx={strongSkeletonSx} />
          <Skeleton variant="text" animation="wave" width="92%" height={22} sx={baseSkeletonSx} />
          <Skeleton variant="text" animation="wave" width="86%" height={22} sx={baseSkeletonSx} />
          <Skeleton variant="text" animation="wave" width="74%" height={22} sx={baseSkeletonSx} />
        </Box>
      </Grid>

      <Grid item xs={12} md={5}>
        <Box sx={cardSectionSx}>
          <Skeleton variant="text" animation="wave" width="48%" height={34} sx={strongSkeletonSx} />
          <Stack spacing={1.25} sx={{ mt: 1 }}>
            <Skeleton variant="rounded" animation="wave" width="100%" height={18} sx={baseSkeletonSx} />
            <Skeleton variant="rounded" animation="wave" width="100%" height={18} sx={baseSkeletonSx} />
            <Skeleton variant="rounded" animation="wave" width="78%" height={18} sx={baseSkeletonSx} />
          </Stack>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={cardSectionSx}>
          <Skeleton variant="text" animation="wave" width="28%" height={34} sx={strongSkeletonSx} />
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={`detail-field-skeleton-${index}`}>
                <Skeleton variant="text" animation="wave" width="40%" height={18} sx={baseSkeletonSx} />
                <Skeleton variant="rounded" animation="wave" width="92%" height={30} sx={strongSkeletonSx} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Grid>
    </Grid>
  </Box>
);

const FormStepperSkeleton = () => (
  <Box sx={{ background: BRAND.accent, p: 4 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', position: 'relative' }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Box key={`stepper-skeleton-${index}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative' }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              border: `3px solid ${brandAlpha('onAccent', 0.9)}`,
              backgroundColor: index === 0 ? BRAND.onAccent : brandAlpha('onAccent', 0.2),
              zIndex: 2,
            }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{
              width: { xs: 56, sm: 96 },
              height: 16,
              mt: 1.5,
              transform: 'none',
              bgcolor: brandAlpha('onAccent', 0.22),
              borderRadius: 99,
            }}
          />
          {index < 3 ? (
            <Box
              sx={{
                position: 'absolute',
                top: 24,
                left: 'calc(50% + 25px)',
                right: 'calc(-50% + 25px)',
                height: 3,
                bgcolor: brandAlpha('onAccent', 0.3),
                zIndex: 1,
              }}
            />
          ) : null}
        </Box>
      ))}
    </Box>
  </Box>
);

const FormSkeletonContent = () => (
  <Box>
    <FormStepperSkeleton />

    <Box sx={{ p: { xs: 2, sm: 4, md: 6 } }}>
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" animation="wave" width="34%" height={38} sx={strongSkeletonSx} />
        <Skeleton variant="text" animation="wave" width="56%" height={22} sx={baseSkeletonSx} />
      </Box>

      <Grid container spacing={3}>
        {Array.from({ length: 6 }).map((_, index) => (
          <Grid item xs={12} md={index < 2 ? 12 : 6} key={`form-field-skeleton-${index}`}>
            <Skeleton variant="text" animation="wave" width="32%" height={18} sx={baseSkeletonSx} />
            <Skeleton variant="rounded" animation="wave" width="100%" height={56} sx={strongSkeletonSx} />
          </Grid>
        ))}
        <Grid item xs={12}>
          <Skeleton variant="text" animation="wave" width="24%" height={18} sx={baseSkeletonSx} />
          <Skeleton variant="rounded" animation="wave" width="100%" height={136} sx={strongSkeletonSx} />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: `1px solid ${BRAND.lineSoft}` }}>
        <Skeleton variant="rounded" animation="wave" width={136} height={48} sx={baseSkeletonSx} />
        <Skeleton variant="rounded" animation="wave" width={148} height={48} sx={strongSkeletonSx} />
      </Box>
    </Box>
  </Box>
);

const AgendaSkeletonContent = ({ includeToolbar = true }) => (
  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
    {includeToolbar ? (
      <Box sx={{ borderBottom: `1px solid ${BRAND.line}`, bgcolor: BRAND.background, px: { xs: 1.25, md: 2 }, py: 1 }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
          <Skeleton variant="rounded" animation="wave" width={220} height={36} sx={strongSkeletonSx} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="rounded" animation="wave" width={140} height={36} sx={strongSkeletonSx} />
            <Skeleton variant="rounded" animation="wave" width={120} height={36} sx={baseSkeletonSx} />
          </Stack>
        </Stack>
      </Box>
    ) : null}

      <Box sx={{ p: 2.5 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Grid item xs={3} key={`agenda-header-skeleton-${index}`}>
              <Skeleton variant="text" animation="wave" width="62%" height={22} sx={strongSkeletonSx} />
              <Skeleton variant="text" animation="wave" width="74%" height={18} sx={baseSkeletonSx} />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ position: 'relative', height: 420, borderRadius: 2, overflow: 'hidden', backgroundColor: BRAND.background }}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Box
              key={`agenda-line-skeleton-${index}`}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: `${(index + 1) * 12}%`,
                height: 1,
                bgcolor: BRAND.line,
              }}
            />
          ))}

          <Skeleton variant="rounded" animation="wave" width="22%" height={74} sx={{ ...strongSkeletonSx, position: 'absolute', top: '15%', left: '14%' }} />
          <Skeleton variant="rounded" animation="wave" width="18%" height={96} sx={{ ...baseSkeletonSx, position: 'absolute', top: '40%', left: '40%' }} />
          <Skeleton variant="rounded" animation="wave" width="20%" height={68} sx={{ ...strongSkeletonSx, position: 'absolute', top: '62%', left: '68%' }} />
        </Box>
      </Box>
  </Box>
);

export const ManagementContentSkeleton = ({ variant = 'cards', withAction = false, cardCount = 6, includeToolbar = true }) => {
  if (variant === 'detail') {
    return <DetailSkeletonContent />;
  }

  if (variant === 'form') {
    return <FormSkeletonContent />;
  }

  if (variant === 'agenda') {
    return <AgendaSkeletonContent includeToolbar={includeToolbar} />;
  }

  return <CardsSkeletonContent withAction={withAction} cardCount={cardCount} />;
};

const ManagementPageSkeleton = ({
  pageTitle,
  pageSubtitle,
  variant = 'cards',
  withAction = false,
  cardCount = 6,
  showLeadingAvatar = false,
  contentSx,
  maxWidth,
  hideHeader = false,
  sx,
  cardSx,
}) => {
  const showHeaderSkeleton = !hideHeader && !pageTitle && !pageSubtitle;

  return (
    <ManagementPageLayout
      pageTitle={hideHeader ? undefined : pageTitle}
      pageSubtitle={hideHeader ? undefined : pageSubtitle}
      leadingContent={showHeaderSkeleton ? <HeaderSkeleton showLeadingAvatar={showLeadingAvatar} /> : null}
      contentSx={contentSx}
      maxWidth={maxWidth}
      animateHeader={false}
      sx={sx}
    >
      <ManagementPageCard sx={cardSx}>
        <ManagementContentSkeleton
          variant={variant}
          withAction={withAction}
          cardCount={cardCount}
        />
      </ManagementPageCard>
    </ManagementPageLayout>
  );
};

export default ManagementPageSkeleton;